import type { DatePreset } from '../types/analytics';

/** The timezone all trading/market timestamps are anchored to. */
export const IST_TZ = 'Asia/Kolkata';

/** Returns today's date in IST as 'YYYY-MM-DD' */
export const getTodayIST = (): string => {
  return new Date().toLocaleDateString('en-CA', { timeZone: IST_TZ });
};

/**
 * Subtract `n` days from a YYYY-MM-DD ISO string, returning a YYYY-MM-DD
 * ISO string. Anchors day arithmetic at noon UTC so DST / IST-offset
 * shifts never cross a calendar boundary. `n` may be negative.
 *
 * Used for marketplace / analytics date-range defaults (e.g. "last 30d"
 * default preset) so both list and detail pages resolve the same window
 * regardless of the viewer's browser timezone.
 */
export const subDaysIso = (iso: string, n: number): string => {
  const [y, m, d] = iso.split('-').map(Number);
  const anchor = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
  anchor.setUTCDate(anchor.getUTCDate() - n);
  return anchor.toISOString().slice(0, 10);
};

/**
 * `{start, end}` for the marketplace-analytics default window (today IST
 * + 29 preceding days = 30-day rolling window). Both list and detail
 * pages import this so they can't drift on the default preset.
 */
export const marketplaceDefault30d = (): { start: string; end: string } => {
  const end = getTodayIST();
  return { start: subDaysIso(end, 29), end };
};

export interface ISTParts {
  yyyy: string; mm: string; dd: string;
  hh: string; min: string; ss: string;
  monShort: string;
}

/**
 * Date + time parts of an instant, evaluated in IST.
 *
 * Backend stores every market timestamp with a +05:30 offset; formatting them
 * with `new Date().getHours()` / `toLocaleTimeString()` (no `timeZone`) renders
 * in the VIEWER's browser timezone, so a trader on GST (+04:00) sees fill times
 * 1:30 behind. Deriving all parts here under `Asia/Kolkata` pins the display to
 * IST regardless of where it's viewed. Throws on an unparseable input — callers
 * that already guard should keep their try/catch.
 */
export function istParts(iso: string): ISTParts {
  const d = new Date(iso);
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: IST_TZ, hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(d).reduce<Record<string, string>>((acc, p) => {
    acc[p.type] = p.value;
    return acc;
  }, {});
  return {
    yyyy: parts.year, mm: parts.month, dd: parts.day,
    // en-GB emits '24' for midnight; normalise to '00'.
    hh: parts.hour === '24' ? '00' : parts.hour,
    min: parts.minute, ss: parts.second,
    monShort: new Intl.DateTimeFormat('en-US', { timeZone: IST_TZ, month: 'short' }).format(d),
  };
}

/**
 * Resolves an analytics date-range preset to `{ start, end }` ISO dates.
 *
 * All math is anchored to *IST* and done in UTC so the result is identical on
 * any machine timezone (dev / CI / Lambda) — the previous local-time +
 * `toISOString()` approach rolled the FY-start back a day across a DST
 * boundary. Indian financial year runs Apr 1 → Mar 31.
 */
export function rangeFromPreset(preset: DatePreset): { start: string; end: string } {
  const end = getTodayIST();                       // 'YYYY-MM-DD' in IST
  const [y, m, d] = end.split('-').map(Number);

  // Noon-UTC anchor: day arithmetic never crosses a boundary, and UTC has no
  // DST, so formatting is stable everywhere.
  const anchor = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
  const fmt = (dt: Date) => dt.toISOString().slice(0, 10);

  switch (preset) {
    case 'Today': return { start: end, end };
    case '7d':  anchor.setUTCDate(anchor.getUTCDate() - 6);  break;
    case '30d': anchor.setUTCDate(anchor.getUTCDate() - 29); break;
    case '90d': anchor.setUTCDate(anchor.getUTCDate() - 89); break;
    case 'FY': {
      // Before April the current FY started last calendar year.
      const fyStartYear = m >= 4 ? y : y - 1;
      return { start: `${fyStartYear}-04-01`, end };
    }
    case 'YTD': return { start: `${y}-01-01`, end };
    case '1Y':  anchor.setUTCFullYear(y - 1); break;
    case 'All': anchor.setUTCFullYear(y - 5); break;
    case 'Custom': return { start: end, end };
  }
  return { start: fmt(anchor), end };
}
